/**
 * HeraldButton — floating envelope pill, fixed bottom-right.
 *
 * Renders on EVERY page (mounted in the App root, outside <Routes>),
 * but quietly returns null when the user is signed out so the marketing
 * site, auth flows, and Try landing remain pristine.
 *
 * Click → opens HeraldPanel (sibling component, mounted lazily so the
 * Compose/Inbox bundles only load on first interaction).
 */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useHerald } from '@/hooks/useHerald';

const HeraldPanel = lazy(() => import('./HeraldPanel'));

export default function HeraldButton() {
  const { user } = useAuth();
  const { unreadCount } = useHerald();
  const [open, setOpen] = useState(false);

  // Cluster I.B (Feb 2026): show the pill on mobile but LIFT it clear
  // of the composer + keyboard. Tracked via state so the gate is
  // honest if the user rotates / opens devtools.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Cluster H.4 (Feb 2026): allow any caller in the app (e.g. the
  // ContractEditor "Send to colleague" toolbar button) to open the
  // Herald panel by dispatching `bayenah:open-herald-panel`. This
  // avoids prop-drilling the panel-open state through the whole
  // page tree.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onOpen = () => setOpen(true);
    window.addEventListener('bayenah:open-herald-panel', onOpen);
    return () => window.removeEventListener('bayenah:open-herald-panel', onOpen);
  }, []);

  // Hide on unauth — the pill is org-aware and would only show errors
  // for a stranger. The marketing site is also intentionally clean.
  // Hook calls above must remain unconditional (Rules of Hooks).
  if (!user) return null;

  const label = unreadCount > 99 ? '99+' : String(unreadCount);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open Herald"
        data-testid="herald-pill"
        // I.B z-index ladder: Herald is layered ABOVE composer
        // (z=40), agent-thinking activity (z=30), tooltips (z=50),
        // but BELOW modals (z=80). var(--z-herald-fab) is defined
        // once in the global stylesheet so the ladder stays sane.
        className={`
          fixed z-[var(--z-herald-fab,60)]
          inline-flex items-center gap-2
          rounded-full bg-[#111111] text-white
          px-4 h-11 text-[13.5px] font-medium
          shadow-[0_4px_16px_rgba(0,0,0,0.3)]
          hover:bg-black transition
          ${isMobile
            // Lifted above the mobile composer (~64px tall) + safe-area inset.
            ? 'bottom-[calc(env(safe-area-inset-bottom,0px)+92px)] right-3'
            : 'bottom-7 right-7'}
        `}
        style={{ direction: 'ltr' }}
      >
        <Mail className="w-4 h-4" />
        <span>Herald</span>
        {unreadCount > 0 && (
          <span
            data-testid="herald-unread-badge"
            className="
              ml-1 inline-flex items-center justify-center
              min-w-[18px] h-[18px] px-1.5
              rounded-full bg-[#00AAFF] text-black
              text-[10.5px] font-bold leading-none
            "
          >
            {label}
          </span>
        )}
      </button>
      {open && (
        <Suspense fallback={null}>
          <HeraldPanel onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}
